import { useCallback, useEffect, useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import {
  KeyDocumentsPage,
  DocumentItem,
} from '@karehero/llama/pwa/pages/KeyDocumentsPage/KeyDocumentsPage';
import KeyDocumentFactsDrawer from '@karehero/llama/pwa/pages/KeyDocumentsPage/KeyDocumentFactsDrawer';
import {
  isCallNoteDocument,
  OrganiseDocumentStatus,
  buildQuestionFormMap,
} from '@karehero/models';

import { isImageType, isAudioType } from '@karehero/helpers/files';

import { useGoBack } from 'hooks/useGoBack';
import { useFeatureFlag } from 'hooks/useFeatureFlags';

import { getCurrentCareCircle } from 'store/slices/careCircleSlice';
import {
  useListOrganiseDocumentsQuery,
  useLazyGetOrganiseDocumentQuery,
  useDeleteOrganiseDocumentMutation,
} from 'store/api/organiseDocument';
import {
  useGetDocumentExtractedFactsQuery,
  useConfirmExtractedFactMutation,
  useDismissExtractedFactMutation,
} from 'store/api/inference';
import { useGetAllCategoryByProfileQuery } from 'store/api/careProfile';

import { ROUTES, DYNAMIC_ROUTES } from 'router/routes';

const needsDownloadUrl = (contentType: string): boolean =>
  isImageType(contentType) || isAudioType(contentType);

export const KeyDocuments = () => {
  const goBack = useGoBack();
  const navigate = useNavigate();
  const careCircle = useSelector(getCurrentCareCircle);
  const careCircleId = careCircle?.id ?? '';
  const careProfileId = careCircle?.careProfile?.id ?? '';
  const careRecipientName =
    careCircle?.careRecipientAccount?.firstName ?? undefined;

  const audioRecordingEnabled = useFeatureFlag('audio-recording-enabled');
  const factsEnabled = useFeatureFlag('aiDataProcessingConsent');

  const [downloadUrls, setDownloadUrls] = useState<Record<string, string>>({});
  const [selectedDoc, setSelectedDoc] = useState<DocumentItem | null>(null);

  const { data: documents = [], isLoading: isLoadingDocs } =
    useListOrganiseDocumentsQuery({ careCircleId }, { skip: !careCircleId });

  const [getDocument] = useLazyGetOrganiseDocumentQuery();
  const [deleteDocument] = useDeleteOrganiseDocumentMutation();

  const { data: factsResponse, isLoading: isLoadingFacts } =
    useGetDocumentExtractedFactsQuery(
      { careCircleId, documentId: selectedDoc?.id ?? '' },
      { skip: !selectedDoc || !careCircleId },
    );

  const [confirmFact] = useConfirmExtractedFactMutation();
  const [dismissFact] = useDismissExtractedFactMutation();

  const { data: categories, isLoading: isLoadingCategories } =
    useGetAllCategoryByProfileQuery(careProfileId, {
      skip: !careProfileId,
    });

  const questionFormMap = useMemo(
    () => buildQuestionFormMap(categories ?? []),
    [categories],
  );

  useEffect(() => {
    if (!careCircleId || !documents.length) return;

    const imageDocuments = documents.filter(
      (doc) =>
        doc.status === OrganiseDocumentStatus.Scanned &&
        needsDownloadUrl(doc.contentType),
    );

    imageDocuments.forEach(async (doc) => {
      if (downloadUrls[doc.id]) return;

      try {
        const result = await getDocument({
          careCircleId,
          documentId: doc.id,
        }).unwrap();

        setDownloadUrls((prev) => ({
          ...prev,
          [doc.id]: result.downloadUrl,
        }));
      } catch (error) {
        console.error('Failed to fetch download URL:', error);
      }
    });
  }, [careCircleId, documents, getDocument, downloadUrls]);

  const documentItems: DocumentItem[] = useMemo(
    () =>
      documents
        .filter((doc) => doc.status !== OrganiseDocumentStatus.Pending)
        .map((doc) => ({
          id: doc.id,
          title: doc.title,
          description: doc.description,
          filename: doc.filename,
          contentType: doc.contentType,
          sizeBytes: doc.sizeBytes,
          status: doc.status,
          tags: doc.tags,
          createdAt: doc.createdAt,
          downloadUrl: downloadUrls[doc.id],
          hasExtractedFacts: Boolean(doc.extractedAt),
          extractedAt: doc.extractedAt,
        })),
    [documents, downloadUrls],
  );

  const handleUpload = useCallback(() => {
    navigate(ROUTES.DOCUMENT_UPLOAD);
  }, [navigate]);

  const handleRecordAudio = useCallback(() => {
    navigate(ROUTES.AUDIO_RECORD);
  }, [navigate]);

  const handleDownload = useCallback(
    async (doc: DocumentItem) => {
      if (!careCircleId) return;

      try {
        if (doc.downloadUrl) {
          window.open(doc.downloadUrl, '_blank');
          return;
        }

        const result = await getDocument({
          careCircleId,
          documentId: doc.id,
        }).unwrap();

        window.open(result.downloadUrl, '_blank');
      } catch (error) {
        console.error('Download failed:', error);
      }
    },
    [careCircleId, getDocument],
  );

  const handleCardClick = useCallback(
    (doc: DocumentItem) => {
      if (isCallNoteDocument(doc.tags)) {
        navigate(DYNAMIC_ROUTES.CALL_NOTE_VIEW(doc.id));
      }
    },
    [navigate],
  );

  const handleDelete = useCallback(
    async (doc: DocumentItem) => {
      if (!careCircleId) return;

      try {
        await deleteDocument({
          careCircleId,
          documentId: doc.id,
        }).unwrap();
        setDownloadUrls((prev) => {
          const next = { ...prev };
          delete next[doc.id];
          return next;
        });
      } catch (error) {
        console.error('Delete failed:', error);
      }
    },
    [careCircleId, deleteDocument],
  );

  const handleConfirmFact = useCallback(
    (factId: string) => {
      if (!careCircleId) return;
      confirmFact({ careCircleId, factId });
    },
    [careCircleId, confirmFact],
  );

  const handleDismissFact = useCallback(
    (factId: string) => {
      if (!careCircleId) return;
      dismissFact({ careCircleId, factId });
    },
    [careCircleId, dismissFact],
  );

  return (
    <>
      <KeyDocumentsPage
        documents={documentItems}
        isLoading={isLoadingDocs}
        onBack={goBack}
        onUpload={handleUpload}
        onRecordAudio={audioRecordingEnabled ? handleRecordAudio : undefined}
        onDownload={handleDownload}
        onDelete={handleDelete}
        onCardClick={handleCardClick}
        showExtractedFacts={Boolean(factsEnabled)}
        onViewFacts={setSelectedDoc}
      />
      <KeyDocumentFactsDrawer
        open={Boolean(selectedDoc)}
        documentTitle={selectedDoc?.title}
        facts={factsResponse?.facts ?? []}
        isLoading={isLoadingFacts || isLoadingCategories}
        careRecipientName={careRecipientName}
        extractedAt={selectedDoc?.extractedAt}
        questionFormMap={questionFormMap}
        onClose={() => setSelectedDoc(null)}
        onConfirm={handleConfirmFact}
        onDismiss={handleDismissFact}
      />
    </>
  );
};

export default KeyDocuments;
